import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppStore } from '@/src/state/app-store';

import {
  isIosSafari,
  isStandalonePwa,
  isWebPlatform,
  type BeforeInstallPromptEvent,
} from './pwa-service';

const snoozeMs = 7 * 24 * 60 * 60 * 1000;

function futureIso(msFromNow: number) {
  return new Date(Date.now() + msFromNow).toISOString();
}

function isFutureIso(value: string | null) {
  if (!value) {
    return false;
  }

  const time = new Date(value).getTime();

  return Number.isFinite(time) && time > Date.now();
}

export function usePwaInstall() {
  const pwaInstallCompletedAt = useAppStore(
    (state) => state.pwaInstallCompletedAt,
  );
  const pwaInstallSnoozedUntil = useAppStore(
    (state) => state.pwaInstallSnoozedUntil,
  );
  const setPwaInstallCompletedAt = useAppStore(
    (state) => state.setPwaInstallCompletedAt,
  );
  const setPwaInstallSnoozedUntil = useAppStore(
    (state) => state.setPwaInstallSnoozedUntil,
  );
  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [standalone, setStandalone] = useState(() => isStandalonePwa());

  useEffect(() => {
    if (!isWebPlatform()) {
      return undefined;
    }

    function handleBeforeInstallPrompt(event: Event) {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    }

    function refreshStandaloneState() {
      setStandalone(isStandalonePwa());
    }

    function handleAppInstalled() {
      setPwaInstallCompletedAt(new Date().toISOString());
      setPwaInstallSnoozedUntil(null);
      refreshStandaloneState();
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window
      .matchMedia?.('(display-mode: standalone)')
      .addEventListener?.('change', refreshStandaloneState);

    refreshStandaloneState();

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
      window.removeEventListener('appinstalled', handleAppInstalled);
      window
        .matchMedia?.('(display-mode: standalone)')
        .removeEventListener?.('change', refreshStandaloneState);
    };
  }, [setPwaInstallCompletedAt, setPwaInstallSnoozedUntil]);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) {
      return false;
    }

    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;

    if (choice.outcome === 'accepted') {
      setPwaInstallCompletedAt(new Date().toISOString());
      setPwaInstallSnoozedUntil(null);
    }

    setInstallPrompt(null);
    setStandalone(isStandalonePwa());

    return choice.outcome === 'accepted';
  }, [installPrompt, setPwaInstallCompletedAt, setPwaInstallSnoozedUntil]);

  const snoozeInstallCard = useCallback(() => {
    setPwaInstallSnoozedUntil(futureIso(snoozeMs));
  }, [setPwaInstallSnoozedUntil]);

  const markInstallDone = useCallback(() => {
    setPwaInstallCompletedAt(new Date().toISOString());
    setPwaInstallSnoozedUntil(null);
  }, [setPwaInstallCompletedAt, setPwaInstallSnoozedUntil]);

  const dismissed =
    Boolean(pwaInstallCompletedAt) || isFutureIso(pwaInstallSnoozedUntil);

  return useMemo(
    () => ({
      canPromptInstall: Boolean(installPrompt),
      dismissed,
      isIosSafari: isIosSafari(),
      isStandalone: standalone,
      isWeb: isWebPlatform(),
      markInstallDone,
      promptInstall,
      snoozeInstallCard,
    }),
    [
      dismissed,
      installPrompt,
      markInstallDone,
      promptInstall,
      snoozeInstallCard,
      standalone,
    ],
  );
}
